<script setup lang="ts">
import { computed } from 'vue'
import type { PuzzlePiece } from '@/composables/game/puzzles'
import { SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import { formatNumberFullTruncate } from '@/helpers/utils'

// Import puzzle piece images
import puzzleElement1 from '@/assets/images/puzzles/puzzle_element1.png'
import puzzleElement2 from '@/assets/images/puzzles/puzzle_element2.png'
import puzzleElement3 from '@/assets/images/puzzles/puzzle_element3.png'
import puzzleElement4 from '@/assets/images/puzzles/puzzle_element4.png'
import puzzleElement5 from '@/assets/images/puzzles/puzzle_element5.png'
import puzzleElement6 from '@/assets/images/puzzles/puzzle_element6.png'
import puzzleElement7 from '@/assets/images/puzzles/puzzle_element7.png'
import puzzleElement8 from '@/assets/images/puzzles/puzzle_element8.png'
import puzzleElement9 from '@/assets/images/puzzles/puzzle_element9.png'
import puzzleElement10 from '@/assets/images/puzzles/puzzle_element10.png'
import puzzleElement11 from '@/assets/images/puzzles/puzzle_element11.png'
import puzzleElement12 from '@/assets/images/puzzles/puzzle_element12.png'

// Import special currency images
import { specialCurrenciesConfig } from '@/config/special-currencies'

const props = defineProps<{
	piece: PuzzlePiece
	index: number
}>()

function getPieceStyle() {
  const col = props.index % 3
  const row = Math.floor(props.index / 3)

  const pieceWidth = 113
  const pieceHeight = 89.25

  return {
    width: `${pieceWidth}px`,
    height: `${pieceHeight}px`,
    position: 'absolute' as const,
    left: `${col * pieceWidth}px`,
    top: `${row * pieceHeight}px`,
  }
}

const pieceImageClasses = computed(() => {
  const classes = []
  const index = props.index

  if (index === 1) {
    classes.push('scale-[1.45]')
    classes.push('origin-top')
  } else if (index === 3) {
    classes.push('origin-top-left')
    classes.push('scale-[1.23]')
  } else if (index === 4) {
    classes.push('origin-bottom-left')
    classes.push('scale-[1.23]')
  } else if (index === 7) {
    classes.push('origin-bottom-right')
    classes.push('scale-[1.23]')
    classes.push('top-1')
    classes.push('left-px')
  } else if (index === 8) {
    classes.push('origin-top-right')
    classes.push('scale-[1.22]')
  } else if (index === 9 || index === 11) {
    classes.push('top-auto')
  } else if (index === 10) {
    classes.push('scale-[1.45]')
    classes.push('origin-top')
    classes.push('-top-px')
  }

  return classes
})

// Map of puzzle piece images
const puzzleImages: Record<number, string> = {
  1: puzzleElement1,
  2: puzzleElement2,
  3: puzzleElement3,
  4: puzzleElement4,
  5: puzzleElement5,
  6: puzzleElement6,
  7: puzzleElement7,
  8: puzzleElement8,
  9: puzzleElement9,
  10: puzzleElement10,
  11: puzzleElement11,
  12: puzzleElement12,
}

const pieceImageSrc = computed(() => puzzleImages[props.index + 1] || puzzleElement1)

const specialCurrencyImageMap: Record<SPECIAL_CURRENCY_ID, string> = {
  [SPECIAL_CURRENCY_ID.GEM]: specialCurrenciesConfig.gem.image,
  [SPECIAL_CURRENCY_ID.CROWN]: specialCurrenciesConfig.crown.image,
  [SPECIAL_CURRENCY_ID.GOLDEN_EGG]: specialCurrenciesConfig.goldenEgg.image,
  [SPECIAL_CURRENCY_ID.KEY]: specialCurrenciesConfig.key.image,
  [SPECIAL_CURRENCY_ID.PARACHUTE]: specialCurrenciesConfig.parachute.image,
  [SPECIAL_CURRENCY_ID.PLANT]: specialCurrenciesConfig.plant.image,
  [SPECIAL_CURRENCY_ID.TOP_HAT]: specialCurrenciesConfig.topHat.image,
  [SPECIAL_CURRENCY_ID.GOLD_FEATHER]: specialCurrenciesConfig.goldFeather.image2,
  [SPECIAL_CURRENCY_ID.HOLIDAY_ORB]: specialCurrenciesConfig.holidayOrb.image,
}

const stepCostLabel = computed(() => {
  const cost = props.piece.step.cost
  if (cost.type === 'free') {
    return 'FREE'
  }
  if (cost.type === 'star') {
    return `$${cost.usdAmount?.toFixed(2)}`
  }
  if (cost.type === 'specialCurrency') {
    return formatNumberFullTruncate(cost.amount)
  }
  return formatNumberFullTruncate(cost as number)
})

</script>

<template>
  <div
    class="puzzle-piece flex items-center justify-center"
    :class="{ 'opacity-0 pointer-none': piece.completed }"
    :style="getPieceStyle()"
  >
    <!-- Puzzle piece image -->
    <img
      :src="pieceImageSrc"
      :alt="`Puzzle piece ${index + 1}`"
      class="absolute inset-0 object-cover"
      :class="pieceImageClasses"
    >
    <!-- Cost display for uncompleted pieces -->
    <div
      class="absolute flex items-center justify-center z-30 h-full w-full"
    >
      <div class="flex items-center justify-center gap-0.5">
        <img
          v-if="piece.step.cost.type === 'specialCurrency' && specialCurrencyImageMap[piece.step.cost.currencyId]"
          class="h-5 w-auto object-cover drop-shadow-[0_0_2px_rgba(0,0,0,0.66)]"
          :src="specialCurrencyImageMap[piece.step.cost.currencyId]"
          :alt="piece.step.cost.currencyId"
        >
        <span class="text-white text-lg font-bold">{{ stepCostLabel }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.puzzle-piece {
	@apply cursor-pointer transition-all relative overflow-visible;
}

.puzzle-piece:hover {
	filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5));
}
</style>